import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import * as authService from '../services/authService';
import websocketService from '../services/websocketService'; // Import WebSocket service

interface User {
  id: string;
  username?: string;
  email: string;
  full_name?: string;
  is_superuser: boolean;
  avatar_url?: string;
  auth_provider?: string;
}

interface AuthConfig {
  local_auth_enabled: boolean;
  oidc_enabled: boolean;
  providers: string[];
  csrf_protection_enabled: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  authConfig: AuthConfig | null;
  isAuthenticated: boolean;
  login: (credentials: { username: string; password: string }) => Promise<void>;
  loginWithProvider: (provider: string) => void;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<User | null>;
  isLoginModalOpen: boolean;
  openLoginModal: () => void;
  closeLoginModal: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  // Load auth configuration
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await authService.getAuthConfig();
        setAuthConfig(config);
      } catch (err) {
        console.error('Failed to load auth config:', err);
        setAuthConfig({
          local_auth_enabled: true,
          oidc_enabled: false,
          providers: [],
          csrf_protection_enabled: false
        });
      }
    };
    
    loadConfig();
  }, []);

  // Check authentication status on mount - but only once
  useEffect(() => {
    let isMounted = true;
    let authCheckStarted = false;
    
    console.log("[AUTH_CONTEXT] Auth context mounted, preparing to check auth status");
    
    const checkAuth = async () => {
      if (!isMounted) {
        console.log("[AUTH_CONTEXT] Component unmounted before auth check started, aborting");
        return;
      }
      
      if (authCheckStarted) {
        console.log("[AUTH_CONTEXT] Auth check already started, preventing duplicate check");
        return;
      }
      
      console.log("[AUTH_CONTEXT] Starting auth status check");
      authCheckStarted = true;
      
      setLoading(true);
      try {
        // Add a cache buster to prevent browser caching
        const cacheBuster = `?_=${Date.now()}`;
        console.log(`[AUTH_CONTEXT] Checking auth status with cache buster: ${cacheBuster}`);
        
        const user = await authService.checkAuthStatus(cacheBuster);
        
        if (isMounted) {
          console.log("[AUTH_CONTEXT] Auth check completed, user:", user ? "authenticated" : "not authenticated");
          setUser(user);
          
          // Enable or disable WebSockets based on authentication status
          if (user) {
            console.log("[AUTH_CONTEXT] User authenticated, enabling WebSockets");
            websocketService.enableWebSockets();
          } else {
            console.log("[AUTH_CONTEXT] User not authenticated, disabling WebSockets");
            websocketService.disableWebSockets();
          }
        } else {
          console.log("[AUTH_CONTEXT] Component unmounted during auth check, not updating state");
        }
      } catch (err) {
        console.error('[AUTH_CONTEXT] Auth check failed:', err);
        if (isMounted) {
          console.log("[AUTH_CONTEXT] Setting user to null due to auth check error");
          setUser(null);
          
          // Disable WebSockets on auth error
          console.log("[AUTH_CONTEXT] Auth check error, disabling WebSockets");
          websocketService.disableWebSockets();
        }
        // Don't show error message for 401 errors - this is expected when not logged in
      } finally {
        if (isMounted) {
          console.log("[AUTH_CONTEXT] Finishing auth check, setting loading to false");
          setLoading(false);
        }
      }
    };
    
    // Small delay to prevent immediate auth check
    console.log("[AUTH_CONTEXT] Scheduling auth check with small delay");
    const timeoutId = setTimeout(checkAuth, 100);
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      console.log("[AUTH_CONTEXT] Auth context unmounting, cleaning up");
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, []); // Empty dependency array ensures this only runs once

  const login = async (credentials: { username: string; password: string }) => {
    setLoading(true);
    setError(null);
    
    try {
      await authService.login(credentials);
      const user = await authService.checkAuthStatus();
      setUser(user);
      
      // Enable WebSockets after successful login
      if (user) {
        console.log("[AUTH_CONTEXT] Login successful, enabling WebSockets");
        websocketService.enableWebSockets();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to login');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loginWithProvider = (provider: string) => {
    authService.loginWithProvider(provider);
  };

  const logout = async () => {
    setLoading(true);
    
    try {
      // Disable WebSockets before logout
      console.log("[AUTH_CONTEXT] Logging out, disabling WebSockets");
      websocketService.disableWebSockets();
      
      await authService.logout();
      setUser(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to logout');
    } finally {
      setLoading(false);
    }
  };

  const checkAuthStatus = async () => {
    setLoading(true);
    
    try {
      const user = await authService.checkAuthStatus();
      setUser(user);
      return user;
    } catch (err) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const openLoginModal = useCallback(() => {
    setIsLoginModalOpen(true);
  }, []);
  
  const closeLoginModal = useCallback(() => {
    setIsLoginModalOpen(false);
  }, []);

  const value = {
    user,
    loading,
    error,
    authConfig,
    isAuthenticated: !!user,
    login,
    loginWithProvider,
    logout,
    checkAuthStatus,
    isLoginModalOpen,
    openLoginModal,
    closeLoginModal
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
